const REQUIRED_VARS = ["VITE_API_BASE_URL", "VITE_ENV"] as const;
const VALID_ENVS = ["dev", "staging", "prod"] as const;
// Quem vai pelo fluxo de redirect da Meta (SA-1339): `mobile` conserta o celular
// sem tocar no desktop, `all` migra todo mundo quando o mobile provar
// estabilidade, `off` é o rollback. Trocar de valor é mudança de env, sem deploy.
const VALID_REDIRECT_SCOPES = ["off", "mobile", "all"] as const;
const DEFAULT_REDIRECT_SCOPE = "mobile";

// URL de fallback do WhatsApp do suporte (usada na AccessBlockedScreen do
// billing). Sobrescrita por VITE_SUPPORT_WHATSAPP_URL quando definida.
const DEFAULT_SUPPORT_WHATSAPP_URL = "https://wa.me/5516936181732";

type RuntimeEnv = {
  VITE_API_BASE_URL: string;
  VITE_S3_PRESIGNED_URL_TTL_IN_MS?: number;
  VITE_ENV: (typeof VALID_ENVS)[number];
  VITE_PUBLIC_POSTHOG_KEY?: string;
  VITE_PUBLIC_POSTHOG_HOST?: string;
  VITE_ENABLE_GOOGLE_AUTH: boolean;
  VITE_SUPPORT_WHATSAPP_URL: string;
  VITE_META_SIGNUP_REDIRECT_SCOPE: (typeof VALID_REDIRECT_SCOPES)[number];
};

function getOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function getRequiredString(
  env: ImportMetaEnv,
  key: (typeof REQUIRED_VARS)[number]
): string {
  const value = getOptionalString(env[key]);

  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }

  return value;
}

function getRuntimeEnv(env: ImportMetaEnv): RuntimeEnv {
  const viteEnv = getRequiredString(env, "VITE_ENV");

  if (!VALID_ENVS.includes(viteEnv as RuntimeEnv["VITE_ENV"])) {
    throw new Error(
      `Invalid environment variable VITE_ENV: ${viteEnv}. Expected one of ${VALID_ENVS.join(", ")}`
    );
  }

  const redirectScope =
    getOptionalString(env.VITE_META_SIGNUP_REDIRECT_SCOPE) ??
    DEFAULT_REDIRECT_SCOPE;

  if (
    !VALID_REDIRECT_SCOPES.includes(
      redirectScope as RuntimeEnv["VITE_META_SIGNUP_REDIRECT_SCOPE"]
    )
  ) {
    throw new Error(
      `Invalid environment variable VITE_META_SIGNUP_REDIRECT_SCOPE: ${redirectScope}. Expected one of ${VALID_REDIRECT_SCOPES.join(", ")}`
    );
  }

  const ttl = getOptionalString(env.VITE_S3_PRESIGNED_URL_TTL_IN_MS);
  const parsedTtl = ttl ? Number(ttl) : undefined;

  if (
    parsedTtl !== undefined &&
    (!Number.isInteger(parsedTtl) || parsedTtl <= 0 || parsedTtl > 604800000)
  ) {
    throw new Error(
      `Invalid VITE_S3_PRESIGNED_URL_TTL_IN_MS: ${ttl}. Expected a positive integer up to 604800000`
    );
  }

  return {
    VITE_API_BASE_URL: getRequiredString(env, "VITE_API_BASE_URL"),
    VITE_ENV: viteEnv as RuntimeEnv["VITE_ENV"],
    ...(parsedTtl !== undefined
      ? { VITE_S3_PRESIGNED_URL_TTL_IN_MS: parsedTtl }
      : {}),
    ...(getOptionalString(env.VITE_PUBLIC_POSTHOG_KEY)
      ? {
          VITE_PUBLIC_POSTHOG_KEY: getOptionalString(
            env.VITE_PUBLIC_POSTHOG_KEY
          ),
        }
      : {}),
    ...(getOptionalString(env.VITE_PUBLIC_POSTHOG_HOST)
      ? {
          VITE_PUBLIC_POSTHOG_HOST: getOptionalString(
            env.VITE_PUBLIC_POSTHOG_HOST
          ),
        }
      : {}),
    VITE_ENABLE_GOOGLE_AUTH: env.VITE_ENABLE_GOOGLE_AUTH === "true",
    VITE_SUPPORT_WHATSAPP_URL:
      getOptionalString(env.VITE_SUPPORT_WHATSAPP_URL) ??
      DEFAULT_SUPPORT_WHATSAPP_URL,
    VITE_META_SIGNUP_REDIRECT_SCOPE:
      redirectScope as RuntimeEnv["VITE_META_SIGNUP_REDIRECT_SCOPE"],
  };
}

export type Env = RuntimeEnv;
export const Env = getRuntimeEnv(import.meta.env);

export function isGoogleAuthEnabled() {
  return Env.VITE_ENABLE_GOOGLE_AUTH;
}

export default Env;
